import { useEffect, useRef, useState } from 'react'
import { useAuthFetch } from '../../lib/useAuthFetch'
import { hasCap, useAccess } from '../../hooks/useAccess'
import { queueTodayRefresh, todayRefreshFinished, todayRefreshSummary, type TodayMenuStatus } from '../../lib/todayRefresh'

const API = import.meta.env.VITE_API_URL ?? ''

export function TodayRefreshControl({ locationIds, onUpdated }: {
  locationIds: string[]
  onUpdated: () => void
}) {
  const authFetch = useAuthFetch()
  const { access, loading: accessLoading } = useAccess()
  const key = [...new Set(locationIds)].sort().join(',')
  const generationRef = useRef(0)
  const queueErrorRef = useRef<string | null>(null)
  const updatedRef = useRef(onUpdated)
  updatedRef.current = onUpdated
  const pendingRef = useRef(false)
  const baselineRef = useRef<TodayMenuStatus[] | null>(null)
  const requestedAtRef = useRef(0)
  const requestingRef = useRef(false)
  const reloadRef = useRef<() => Promise<void>>(async () => {})
  const [statuses, setStatuses] = useState<TodayMenuStatus[]>([])
  const [checking, setChecking] = useState(true)
  const [queueing, setQueueing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    generationRef.current++
    const ids = key ? key.split(',') : []
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | undefined
    let sequence = 0
    pendingRef.current = false
    baselineRef.current = null
    queueErrorRef.current = null
    requestingRef.current = false
    setQueueing(false)
    setStatuses([])
    setError(null)
    setChecking(ids.length > 0)
    const load = async () => {
      const request = ++sequence
      if (timer) clearTimeout(timer)
      if (!ids.length) { setChecking(false); return }
      try {
        const next = await Promise.all(ids.map(async id => {
          const response = await authFetch(`${API}/api/v1/menu-refresh/locations/${encodeURIComponent(id)}`, { signal: controller.signal })
          if (!response.ok) throw new Error('status unavailable')
          const body = await response.json()
          if (!body.data) throw new Error('status unavailable')
          return body.data as TodayMenuStatus
        }))
        if (controller.signal.aborted || request !== sequence) return
        const summary = todayRefreshSummary(next)
        const waiting = pendingRef.current && baselineRef.current !== null && !todayRefreshFinished(baselineRef.current, next)
        const updating = summary.updating || waiting
        const completed = pendingRef.current && !updating
        pendingRef.current = updating
        if (completed) baselineRef.current = null
        setStatuses(next)
        const slow = updating && requestedAtRef.current > 0 && Date.now() - requestedAtRef.current > 10 * 60_000
        setError(slow ? 'Some menus are taking longer to update. Check again.' : queueErrorRef.current)
        if (completed) updatedRef.current()
        if (updating && !slow) timer = setTimeout(load, 10_000)
      } catch {
        if (controller.signal.aborted || request !== sequence) return
        setError('Couldn’t check the update. Try again.')
        if (pendingRef.current && Date.now() - requestedAtRef.current < 10 * 60_000) timer = setTimeout(load, 10_000)
      } finally {
        if (!controller.signal.aborted && request === sequence) setChecking(false)
      }
    }
    reloadRef.current = load
    void load()
    return () => { generationRef.current++; controller.abort(); if (timer) clearTimeout(timer) }
  }, [key, authFetch])

  const requestUpdate = async () => {
    if (requestingRef.current) return
    if (pendingRef.current) { void reloadRef.current(); return }
    const ids = key ? key.split(',') : []
    if (!ids.length) return
    const generation = generationRef.current
    requestingRef.current = true
    queueErrorRef.current = null
    setQueueing(true)
    setError(null)
    try {
      const result = await queueTodayRefresh(ids, authFetch, API)
      if (generationRef.current !== generation) return
      pendingRef.current = result.pending
      baselineRef.current = result.pending ? statuses : null
      requestedAtRef.current = Date.now()
      await reloadRef.current()
      if (generationRef.current !== generation) return
      if (result.failed) queueErrorRef.current = 'Some updates couldn’t be requested. Try again.'
      else if (!result.pending) queueErrorRef.current = 'These menus need review before they can be updated.'
      if (queueErrorRef.current) setError(queueErrorRef.current)
    } catch {
      if (generationRef.current === generation) setError('Couldn’t request the update. Try again.')
    } finally {
      if (generationRef.current === generation) {
        requestingRef.current = false
        setQueueing(false)
      }
    }
  }

  const summary = todayRefreshSummary(statuses)
  const updating = summary.updating || pendingRef.current
  const timestamp = summary.lastUpdated
    ? new Date(summary.lastUpdated).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null

  return (
    <div className="today-refresh-control" data-testid="today-refresh-control" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 12px', marginTop: 8, fontSize: 11, color: 'var(--text-3)' }}>
      <span title="Latest completed rival menu update for the selected locations">{checking ? 'Checking last update…' : timestamp ? <>Last updated <time dateTime={summary.lastUpdated!}>{timestamp}</time></> : 'Last updated: not available yet'}</span>
      {hasCap(access, 'manage_tracks') && (
        <button
          type="button"
          onClick={() => void requestUpdate()}
          disabled={checking || queueing || accessLoading || !key || (updating && !error)}
          title={locationIds.length === 1 ? 'Update menus for this location' : 'Update menus for all selected locations'}
          style={{ padding: '4px 9px', borderRadius: 6, border: '1px solid var(--border-2)', background: 'var(--surface)', color: 'var(--accent)', font: 'inherit', fontWeight: 600, cursor: checking || queueing || updating ? 'default' : 'pointer' }}
        >{queueing ? 'Requesting…' : updating ? error ? 'Check update' : 'Updating…' : 'Update now'}</button>
      )}
      <span role="status" style={{ color: error ? 'var(--text-2)' : undefined }}>
        {error || (updating ? 'Update in progress' : '')}
      </span>
    </div>
  )
}
